/**
 * Where this build thinks it lives, and whether it wants to be found.
 *
 * Both values are build-time. That is deliberate: a canonical URL derived from
 * `window.location.origin` would happily canonicalise the site to whatever host
 * served it — including the `*.cloudfront.net` name — and CloudFront names are
 * exactly the kind of thing that ends up in a search index next to the real
 * domain. Naming the host in the build makes the CloudFront name canonicalise
 * to the real one instead of competing with it.
 */

/** No trailing slash, ever — every caller appends a path that starts with "/". */
export const SITE_URL = (import.meta.env.VITE_SITE_URL ?? "https://runitpastkev.com").replace(
  /\/+$/,
  "",
);

/**
 * Whether crawlers may index this build.
 *
 * Defaults to FALSE. An unset variable means "some build nobody configured",
 * and the safe reading of that is a preview or a throwaway bundle, not the
 * public site. The cost of the two failure directions is not symmetric: a
 * missing noindex puts a staging copy in Google against the real domain and
 * takes weeks to unwind; a stray noindex is a one-line fix.
 *
 * The seoAssets plugin in vite.config.ts reads the same variable for robots.txt,
 * the sitemap AND the static `<meta name="robots">` in index.html, so all four
 * answers come from one place and cannot disagree.
 *
 * That last one was the gap. This docstring used to name a
 * `scripts/build-seo-assets.mjs` that no longer exists and claimed robots.txt
 * and the meta tag could not disagree — true of the tag usePageMeta writes at
 * runtime, false of the one shipped in the document, which was hardcoded to
 * `noindex, nofollow`. Every crawler that does not execute JavaScript read the
 * hardcoded one.
 */
export const INDEXABLE = import.meta.env.VITE_INDEXABLE === "on";

/** Absolute URL for a site-relative path. */
export function absoluteUrl(path: string): string {
  return `${SITE_URL}${path.startsWith("/") ? path : `/${path}`}`;
}
